import React, { useState, useEffect, useMemo } from "react";
import authStorage from "../../utils/authStorage";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import axios from "axios";
import { BASE_URL } from "../../config";

/** Frontend keys (only used to init Stripe; backend creates PI) */
const PUBLISHABLE_KEYS = {
  test: "pk_test_51N66M3SD7QKOKH3BjS6sufqdUyjfq1jiM8bsm5AerUAHUIoJOatJw0LEhc2WBNtv3lqp13mkXQaMtZkR19aDEk3t001hb1ZdmK",
  live: "pk_live_P1FhfWoh7B5aEP7ycjFGbrBE", // ← put a real pk_live_... here for live
};

/** Simple admin check (as per your code) */
const getIsAdmin = () => ["admin"].includes(authStorage.getItem("roles"));

const StripePaymentTraining = ({ setPaymentPage, setPaymentPageLoaded, paymentType }) => {
  const isAdmin = getIsAdmin();

  // Admin can toggle; others locked to live
  const [uiMode, setUiMode] = useState("live");

  // Server-confirmed mode; used to pick pk to avoid mismatch
  const [serverMode, setServerMode] = useState(null); // "live" | "test" | null

  const [clientSecret, setClientSecret] = useState("");
  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const requestedMode = isAdmin ? uiMode : "live";

  const effectiveModeForPK = serverMode || requestedMode;
  const publishableKey = PUBLISHABLE_KEYS[effectiveModeForPK];

  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);

  useEffect(() => {
	
	authStorage.setItem("striptMode", uiMode);
	  
    if (!paymentType) return;

    setClientSecret("");
    setPaymentIntentId("");
    setServerMode(null);

    axios({
      url: `${BASE_URL}/auth/create/onetime/payment-intent`,
      method: "POST",
      headers: {
        "x-access-token": authStorage.getItem("auth-token"),
        "x-stripe-mode": requestedMode, // backend reads header if body.mode missing
      },
      data: { paymentType },
    })
      .then((res) => {
        const { clientSecret, paymentIntentId, mode } = res.data || {};
        if (!clientSecret) throw new Error("Missing clientSecret from server");
        setClientSecret(clientSecret);
        if (paymentIntentId) setPaymentIntentId(paymentIntentId);
        if (mode === "live" || mode === "test") setServerMode(mode);
      })
      .catch((err) => {
        console.error("Failed to create PI:", err?.response?.data || err.message);
      });
  }, [paymentType, requestedMode]);

  const appearance = {
    theme: "stripe",
    variables: {
      colorPrimary: "#26455A",
      colorBackground: "#fff",
      colorText: "#000",
      colorDanger: "#df1b41",
      fontFamily: "Ideal Sans, system-ui, sans-serif",
      spacingUnit: "3.5px",
      borderRadius: "0px",
      border: "1px solid #ffffff38",
    },
  };

  return (
    <div className="pt-6 px-14">
      {/* Admin-only mode selector */}
      {isAdmin && (
        <div className="flex justify-center mb-5">
          <label className="font-medium" style={{ marginRight: 10 }}>
            Mode:
          </label>

          <select
            value={uiMode}
            onChange={(e) => setUiMode(e.target.value)}
            className="border px-2 py-1 rounded"
            style={{ marginRight: 10 }}
          >
            <option value="test">Test</option>
            <option value="live">Live</option>
          </select>

          <span
            className={`text-xs px-2 py-1 rounded ${
              (serverMode || uiMode) === "live" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}
          >
            {(serverMode || uiMode) === "live" ? "LIVE — real charges" : "TEST — no real charges"}
          </span>
        </div>
      )}

      {clientSecret && stripePromise && (
        <Elements
          key={`${publishableKey}:${clientSecret}`} // force remount on mode switch
          stripe={stripePromise}
          options={{ appearance, clientSecret }}
        >
          <CheckoutForm
            setName={setName}
            name={name}
            setEmail={setEmail}
            email={email}
            paymentType={paymentType}
            setPaymentPage={setPaymentPage}
            clientSecret={clientSecret}
            paymentIntent={paymentIntentId}
          />
        </Elements>
      )}
    </div>
  );
};

export default StripePaymentTraining;
